import { useMutation, useQuery } from '@tanstack/react-query';
import { apiClient } from './client';
import type { TradingDiaryNoteResponse, TradingDiaryResponse } from '@/types/api';

export interface TradingDiaryParams {
  station: string;
  startDate: string;
  endDate: string;
}

export interface TradingDiaryNotePayload {
  pre_judgement: string;
  intraday_action: string;
  deviation_reason: string;
  self_score: number | null;
}

export interface TradingDiaryNoteMutationParams extends TradingDiaryNotePayload {
  station: string;
  date: string;
}

export function useTradingDiary(params: TradingDiaryParams) {
  return useQuery({
    queryKey: ['trading-diary', params.station, params.startDate, params.endDate],
    queryFn: async () => {
      const res = await apiClient.get<TradingDiaryResponse>('/trading-diary/data', {
        params: {
          station: params.station,
          start_date: params.startDate,
          end_date: params.endDate,
        },
      });
      return res.data;
    },
    enabled: Boolean(params.station && params.startDate && params.endDate),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

export async function saveTradingDiaryNote(params: TradingDiaryNoteMutationParams) {
  const { station, date, ...payload } = params;
  const res = await apiClient.put<TradingDiaryNoteResponse>('/trading-diary/note', payload, {
    params: { station, date },
  });
  return res.data;
}

export async function completeTradingDiaryNote(params: TradingDiaryNoteMutationParams) {
  const { station, date, ...payload } = params;
  const res = await apiClient.post<TradingDiaryNoteResponse>('/trading-diary/note/complete', payload, {
    params: { station, date },
  });
  return res.data;
}

export function useSaveTradingDiaryNote() {
  return useMutation({
    mutationFn: saveTradingDiaryNote,
  });
}

export function useCompleteTradingDiaryNote() {
  return useMutation({
    mutationFn: completeTradingDiaryNote,
  });
}

export interface TradingDiaryImportPayload {
  station: string;
  date: string;
  note: TradingDiaryNotePayload;
  review_strategy: {
    strategy_type: string;
    strategy_name?: string | null;
    initial_soc_mwh?: number | null;
    initial_soc_percent?: number | null;
    actions?: Array<Record<string, unknown>>;
    carryover_soc?: unknown;
    raw_text: string;
    uncertainties?: string[];
    status?: string;
    source?: Record<string, unknown> | null;
  };
}

export interface TradingDiaryImportResponse {
  code: string;
  message: string;
  data: {
    note: Record<string, unknown>;
    strategy: Record<string, unknown>;
    operation: string;
  };
}

export async function importTradingDiary(params: TradingDiaryImportPayload) {
  const { station, date, ...body } = params;
  const res = await apiClient.post<TradingDiaryImportResponse>('/trading-diary/import', body, {
    params: { station, date },
  });
  return res.data;
}

export function useImportTradingDiary() {
  return useMutation({
    mutationFn: importTradingDiary,
  });
}
